
'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/context/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AppLogo } from '@/components/icons';
import { Loader2, Mail } from 'lucide-react';
import { PricingCard } from '@/components/pricing-card';
import { useToast } from '@/hooks/use-toast';
import { TikTokEmbed } from 'react-social-media-embed';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const GoogleIcon = () => (
  <svg className="size-5" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.619-3.317-11.28-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C39.988,36.623,44,30.836,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
  </svg>
);

export function LoginPageContent() {
  const { user, loginWithGoogle, loginWithEmail, loading, error } = useAuth();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showGmailNudge, setShowGmailNudge] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (typeof window !== 'undefined' && localStorage.getItem('officialspay-has-logged-in')) {
      setIsReturningUser(true);
    }
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem('officialspay-has-logged-in', 'true');
      router.push('/');
    }
  }, [user, router]);

  const isGmailAddress = (addr: string) =>
    addr.trim().toLowerCase().endsWith('@gmail.com');

  const handleEmailContinue = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isGmailAddress(email)) {
      setShowGmailNudge(true);
      return;
    }
    await sendMagicLink();
  };

  const sendMagicLink = async () => {
    await loginWithEmail(email);
    setMagicLinkSent(true);
    toast({
      title: "Check your email",
      description: `We've sent a magic link to ${email}.`,
    });
  };

  const handleGoogleFromDialog = async () => {
    setShowLoginDialog(false);
    await loginWithGoogle();
  };

  const resetDialogState = () => {
    setEmail('');
    setShowGmailNudge(false);
    setMagicLinkSent(false);
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-background">
      <main className="flex w-full flex-1 flex-col items-center justify-center p-4">
        <div className="mb-8">
          <AppLogo iconSize="size-10" textSize="text-2xl" />
        </div>
        <div className="w-full max-w-6xl">
          <div className="flex flex-col items-center text-center gap-4 mb-8">
            <h1 className="text-4xl lg:text-5xl font-bold font-headline leading-tight">
              You Focus on the Call.<br />We Focus on the Cash.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Finally, an app built just for umpires. Log games, track payments, and get valuable insights without the spreadsheet headache.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16">
            <div className="w-full max-w-sm mx-auto flex items-center justify-center">
              <TikTokEmbed url="https://www.tiktok.com/@bestumpireshow/video/7523933157301685534" width="100%" />
            </div>

            <div className="flex flex-col gap-8 w-full max-w-md mx-auto">
              {isReturningUser && (
                <Button size="lg" className="w-full" onClick={() => { resetDialogState(); setShowLoginDialog(true); }}>
                  Log In
                </Button>
              )}
              <PricingCard onGetStarted={() => { resetDialogState(); setShowLoginDialog(true); }} />
              {!isReturningUser && (
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 text-center"
                  onClick={() => { resetDialogState(); setShowLoginDialog(true); }}
                >
                  Already have an account? Log in
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
      <footer className="w-full p-8 text-center text-muted-foreground">
        <p>Brought to you by <a href="https://www.tiktok.com/@bestumpireshow" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Best Umpire Show</a></p>
        <p className="mt-2 text-xs">
          <a href="/terms" className="hover:text-foreground underline">Terms of Service</a>
          <span className="mx-2">·</span>
          <a href="/privacy" className="hover:text-foreground underline">Privacy Policy</a>
        </p>
      </footer>

      {/* ---------- Login Chooser Dialog ---------- */}
      <Dialog open={showLoginDialog} onOpenChange={(open) => { setShowLoginDialog(open); if (!open) resetDialogState(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Sign in to OfficialsPay</DialogTitle>
            <DialogDescription className="text-center">
              Choose how you would like to sign in.
            </DialogDescription>
          </DialogHeader>

          {magicLinkSent ? (
            /* ---- Magic link confirmation ---- */
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="flex items-center justify-center size-12 rounded-full bg-primary/10">
                <Mail className="size-6 text-primary" />
              </div>
              <p className="text-center text-sm text-muted-foreground">
                We sent a magic link to <strong>{email}</strong>. Check your inbox and click the link to sign in.
              </p>
              <Button variant="outline" className="w-full" onClick={() => { setMagicLinkSent(false); setShowGmailNudge(false); }}>
                Use a different method
              </Button>
            </div>
          ) : showGmailNudge ? (
            /* ---- Gmail nudge ---- */
            <div className="flex flex-col gap-4 py-2">
              <p className="text-sm text-center text-muted-foreground">
                This looks like a Google account. Sign in with Google for the best experience.
              </p>
              <Button onClick={handleGoogleFromDialog} disabled={loading} size="lg" className="w-full">
                {loading ? <Loader2 className="animate-spin" /> : <><GoogleIcon /><span className="ml-2">Sign in with Google</span></>}
              </Button>
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 text-center"
                onClick={sendMagicLink}
                disabled={loading}
              >
                Send magic link instead
              </button>
            </div>
          ) : (
            /* ---- Default: Google + Email ---- */
            <div className="flex flex-col gap-5 py-2">
              <Button onClick={handleGoogleFromDialog} disabled={loading} variant="outline" size="lg" className="w-full">
                {loading ? <Loader2 className="animate-spin" /> : <><GoogleIcon /><span className="ml-2">Sign in with Google</span></>}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <form onSubmit={handleEmailContinue} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email address</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full" size="lg">
                  {loading ? <Loader2 className="animate-spin" /> : 'Continue with Email'}
                </Button>
              </form>

              {error && <p className="text-destructive text-sm text-center">{error}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
